// Cookie consent. Audience measurement (Google Analytics, Microsoft Clarity) only loads after the visitor accepts, and
// stops when they refuse. Nothing here runs on the server, so the prerendered marketing pages are untouched.

const KEY = "ernest-cookie-consent";
const GA_ID = "G-GL723DLG47";
const CLARITY_ID = "wmcniejmwm";

export type Consent = "granted" | "denied";

export const OPEN_SETTINGS_EVENT = "ernest:open-cookie-settings";
export const CONSENT_EVENT = "ernest:consent-change";

type Win = Record<string, unknown>;
const w = (): Win => window as unknown as Win;
const call = (name: string, ...args: unknown[]) => {
  const fn = w()[name];
  if (typeof fn === "function") (fn as (...a: unknown[]) => void)(...args);
};

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

let loaded = false;

function loadAnalytics() {
  if (loaded) return;
  loaded = true;

  // Google Analytics
  const win = w();
  win.dataLayer = (win.dataLayer as unknown[] | undefined) ?? [];
  win.gtag = function () {
    // gtag reads the `arguments` object itself, a rest array does not work.
    // eslint-disable-next-line prefer-rest-params
    (win.dataLayer as unknown[]).push(arguments);
  };
  call("gtag", "js", new Date());
  call("gtag", "config", GA_ID);
  const ga = document.createElement("script");
  ga.async = true;
  ga.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(ga);

  // Microsoft Clarity
  win.clarity =
    win.clarity ??
    function () {
      // eslint-disable-next-line prefer-rest-params
      const args = arguments;
      const clarity = win.clarity as { q?: unknown[] };
      (clarity.q = clarity.q ?? []).push(args);
    };
  const cl = document.createElement("script");
  cl.async = true;
  cl.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
  document.head.appendChild(cl);
}

/** Removes the cookies the two tools set, on this host and its parent domain. */
function clearAnalyticsCookies() {
  const names = document.cookie.split(";").map((c) => c.split("=")[0].trim());
  const mine = names.filter((n) => /^(_ga|_gid|_gat|_clck|_clsk|CLID|ANONCHK|MR|MUID|SM|SRM_B)/.test(n));
  const host = location.hostname;
  const parts = host.split(".");
  const domains = [host, `.${host}`, parts.length > 2 ? `.${parts.slice(-2).join(".")}` : ""].filter(Boolean);
  for (const n of mine) {
    for (const d of domains) document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
    document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

export function setConsent(value: Consent) {
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* private mode: the choice lasts until the page closes */
  }
  if (value === "granted") {
    w()[`ga-disable-${GA_ID}`] = false;
    loadAnalytics();
    call("clarity", "consent", true);
  } else {
    w()[`ga-disable-${GA_ID}`] = true;
    call("clarity", "consent", false);
    clearAnalyticsCookies();
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

/** Call once at startup: loads the tools right away for a visitor who already accepted. */
export function initConsent() {
  if (getConsent() === "granted") loadAnalytics();
}

/** Opens the banner again, from a "Cookie settings" link. */
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}
